import classnames from "classnames";
import { Icon } from "common/Icon";
import _ from "lodash";
import { easing, object, tween } from "popmotion";
import React from "react";
import styles from "./HorizontalTabs.scss";

const arrowRadius = 30; // needs to match value in HorizontalTabs.scss

// Takes three props: defaultItem, items, and onSelected
//
// items should be an array of {key, node, width} objects, where
// node should be the contents of the tab and key is the
// value passed to the callback when the tab is clicked.
// width is optional, and can be used to make the tab fixed-width
//
// selected should be the key of the currently selected tab
//
// onClick will be called with the key of the item the user has clicked
export default class HorizontalTabs extends React.Component {
  constructor(props) {
    super(props);
    this.onScroll = _.throttle(this.onScroll, 20);
    this.state = { leftArrow: false, rightArrow: true };
  }

  componentDidMount() {
    // make sure all repaints are done to avoid race conditions
    window.requestAnimationFrame(() => {
      this.onScroll(); // re-compute the arrows
    });
    window.addEventListener("resize", this.onScroll);
  }

  onScroll = () => {
    const tabbox = this._tabbox;
    this.setState({
      leftArrow: this._tabbox.scrollLeft !== 0,
      rightArrow: tabbox.scrollWidth - tabbox.scrollLeft !== tabbox.clientWidth,
    });
  };

  // This works GREAT as long as all the child elements in the tab bar are
  // the same width. It's not so great when we override width on one element
  // (i.e. the 'show all' button)
  scrollRight = () => {
    const tabbox = this._tabbox;
    const childWidth = outerWidth(tabbox.children[0]);
    // scroll by multiples of childWidth, with an offset of arrowRadius plus 5px padding
    const scrollTo =
      Math.floor((tabbox.scrollLeft + arrowRadius + 5) / childWidth) * childWidth + childWidth - (arrowRadius + 5);
    const rightTween = tween({
      adapter: object,
      element: tabbox,
      values: {
        scrollLeft: {
          to: scrollTo,
          easing: easing.quartOut,
        },
      },
    });
    rightTween.on(this._tabbox).start();
  };

  scrollLeft = () => {
    const tabbox = this._tabbox;
    const childWidth = outerWidth(tabbox.children[0]);
    // scroll by multiples of childWidth, with an offset of arrowRadius plus 5px padding
    const scrollTo =
      Math.floor((tabbox.scrollLeft + arrowRadius + 5) / childWidth) * childWidth - childWidth - (arrowRadius + 5);
    const leftTween = tween({
      adapter: object,
      element: tabbox,
      values: {
        scrollLeft: {
          to: scrollTo,
          easing: easing.quartOut,
        },
      },
    });
    leftTween.on(this._tabbox).start();
  };

  render() {
    const { tabs, selected, onClick } = this.props;
    const { leftArrow, rightArrow } = this.state;
    return (
      <div
        className={styles.Wrapper}
        ref={(r) => {
          this._wrapper = r;
        }}
      >
        {leftArrow && (
          <div className={styles.arrowLeft} onClick={this.scrollLeft}>
            <Icon i="ios-arrow-back" size={20} />
          </div>
        )}
        <div
          className={styles.HorizontalTabs}
          onScroll={this.onScroll}
          ref={(r) => {
            this._tabbox = r;
          }}
        >
          {tabs.map((tab) => (
            <Tab onClick={onClick} key={tab.key} tabKey={tab.key} selected={tab.key === selected} width={tab.width}>
              {tab.node}
            </Tab>
          ))}
        </div>
        {rightArrow && (
          <div className={styles.arrowRight} onClick={this.scrollRight}>
            <Icon i="ios-arrow-forward" size={20} />
          </div>
        )}
      </div>
    );
  }
}

export function Tab({ onClick, tabKey, children, width, selected }) {
  const curriedOnClick = () => onClick(tabKey);
  const style = width ? { flex: `0 0 ${width}px`, minWidth: width } : {};
  const className = classnames(styles.Tab, { [styles.SelectedItem]: selected });
  return (
    <div className={className} onClick={curriedOnClick} style={style}>
      {children}
    </div>
  );
}

function outerWidth(el) {
  var width = el.offsetWidth;
  var style = window.getComputedStyle(el);

  width += parseInt(style.marginLeft) + parseInt(style.marginRight);
  return width;
}
